import { useState, useRef, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { KordaroLogo } from '../../../core/shared/components/KordaroLogo.js';
import { KIcon, type KIconNaam } from '../../../core/shared/components/KIcon.js';
import { documentType } from '../utils/portaalWeergave.js';

// === Types ===

interface SigningRequestPublic {
  id: string;
  dossierId: string;
  documentId: string;
  type: 'CANVAS' | 'ITSME';
  status: 'PENDING' | 'SIGNED' | 'EXPIRED' | 'CANCELLED';
  signedAt: string | null;
  expiresAt: string;
  createdAt: string;
  document: {
    id: string;
    naam: string;
    documentType: string | null;
    mimeType: string;
  };
  requestedBy: {
    firstName: string;
    lastName: string;
  };
  dossier: {
    id: string;
    dossierNummer: string;
    klantNaam: string;
  };
}

// === API hooks ===

function usePublicSigning(signingId: string, token: string) {
  return useQuery({
    queryKey: ['signing', 'public', signingId],
    queryFn: async (): Promise<SigningRequestPublic> => {
      const res = await fetch(`/api/signing/public/${signingId}?token=${token}`);
      if (!res.ok) throw new Error('Verzoek niet gevonden');
      return res.json();
    },
    enabled: !!signingId && !!token,
  });
}

function useSignCanvas(signingId: string, token: string) {
  return useMutation({
    mutationFn: async (signatureData: string) => {
      const res = await fetch(`/api/signing/public/${signingId}/sign-canvas?token=${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ signatureData }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || 'Ondertekenen mislukt');
      }
      return res.json();
    },
  });
}

// === Canvas Component (simplified for klantportaal) ===

interface SignatureCanvasRef {
  toDataURL: () => string;
  isEmpty: () => boolean;
  clear: () => void;
}

function PortaalSignatureCanvas({ onReady }: { onReady: (ref: SignatureCanvasRef) => void }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawingRef = useRef(false);
  const lastPosRef = useRef<{ x: number; y: number } | null>(null);
  const undoStackRef = useRef<ImageData[]>([]);
  const redoStackRef = useRef<ImageData[]>([]);
  const strokeCountRef = useRef(0);

  const getCtx = useCallback(() => canvasRef.current?.getContext('2d') || null, []);

  const saveState = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = getCtx();
    if (!canvas || !ctx) return;
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    undoStackRef.current.push(imageData);
    if (undoStackRef.current.length > 30) undoStackRef.current.shift();
    redoStackRef.current = [];
  }, [getCtx]);

  const getPos = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  }, []);

  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.setPointerCapture(e.pointerId);
    saveState();
    isDrawingRef.current = true;
    lastPosRef.current = getPos(e);
    strokeCountRef.current++;
  }, [getPos, saveState]);

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isDrawingRef.current || !lastPosRef.current) return;
    const ctx = getCtx();
    if (!ctx) return;
    const pos = getPos(e);
    const pressure = e.pressure > 0 ? e.pressure : 0.5;
    ctx.beginPath();
    ctx.moveTo(lastPosRef.current.x, lastPosRef.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = '#1e293b';
    ctx.lineWidth = 2 + pressure * 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
    lastPosRef.current = pos;
  }, [getCtx, getPos]);

  const handlePointerUp = useCallback(() => {
    isDrawingRef.current = false;
    lastPosRef.current = null;
  }, []);

  const undo = useCallback(() => {
    const ctx = getCtx();
    const canvas = canvasRef.current;
    if (!ctx || !canvas || undoStackRef.current.length === 0) return;
    const current = ctx.getImageData(0, 0, canvas.width, canvas.height);
    redoStackRef.current.push(current);
    const prev = undoStackRef.current.pop()!;
    ctx.putImageData(prev, 0, 0);
  }, [getCtx]);

  const redo = useCallback(() => {
    const ctx = getCtx();
    const canvas = canvasRef.current;
    if (!ctx || !canvas || redoStackRef.current.length === 0) return;
    const current = ctx.getImageData(0, 0, canvas.width, canvas.height);
    undoStackRef.current.push(current);
    const next = redoStackRef.current.pop()!;
    ctx.putImageData(next, 0, 0);
  }, [getCtx]);

  const clear = useCallback(() => {
    const ctx = getCtx();
    const canvas = canvasRef.current;
    if (!ctx || !canvas) return;
    saveState();
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    strokeCountRef.current = 0;
  }, [getCtx, saveState]);

  // Expose ref methods
  const refCallback = useCallback((canvas: HTMLCanvasElement | null) => {
    if (!canvas) return;
    (canvasRef as React.MutableRefObject<HTMLCanvasElement | null>).current = canvas;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      canvas.width = canvas.offsetWidth * 2;
      canvas.height = canvas.offsetHeight * 2;
    }
    onReady({
      toDataURL: () => canvas.toDataURL('image/png'),
      isEmpty: () => strokeCountRef.current < 2,
      clear,
    });
  }, [clear, onReady]);

  return (
    <div>
      <canvas
        ref={refCallback}
        role="img"
        aria-label="Tekenveld voor uw handtekening"
        className="w-full bg-white border border-[var(--k-control-border)] rounded-[var(--k-radius-control)] cursor-crosshair touch-none"
        style={{ height: '200px' }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
      />
      <div className="flex flex-wrap items-center justify-between gap-1 mt-2">
        <button type="button" onClick={undo} className="k-button k-button--text">Ongedaan maken</button>
        <button type="button" onClick={redo} className="k-button k-button--text">Herhalen</button>
        <button type="button" onClick={clear} className="k-button k-button--text">Wissen</button>
      </div>
    </div>
  );
}

// === Melding (laden / ongeldig / verlopen) ===

function Melding({ icoon, titel, children, laden }: { icoon?: KIconNaam; titel: string; children?: ReactNode; laden?: boolean }) {
  return (
    <div className="k-ui min-h-[100dvh] flex items-center justify-center px-4">
      <div className="text-center max-w-md" role={laden ? 'status' : undefined} aria-busy={laden || undefined}>
        {icoon && <span className="inline-flex text-[var(--k-muted)] mb-4"><KIcon naam={icoon} size={40} /></span>}
        {laden && <span className="block w-10 h-10 mx-auto mb-4 rounded-full border-2 border-[var(--k-border)] border-t-[var(--k-brand)] animate-spin motion-reduce:animate-none" aria-hidden="true" />}
        <h1 className="k-section-title mb-2">{titel}</h1>
        {children && <p>{children}</p>}
      </div>
    </div>
  );
}

// === Main Page ===

export function SigningPage() {
  const { token, signingId } = useParams<{ token: string; signingId: string }>();
  const { data: signing, isLoading, error, refetch } = usePublicSigning(signingId || '', token || '');
  const signCanvas = useSignCanvas(signingId || '', token || '');
  const [canvasRef, setCanvasRef] = useState<SignatureCanvasRef | null>(null);
  const [signed, setSigned] = useState(false);
  const [validationError, setValidationError] = useState('');

  const handleSign = async () => {
    if (!canvasRef) return;
    setValidationError('');

    if (canvasRef.isEmpty()) {
      setValidationError('Teken eerst uw handtekening in het veld.');
      return;
    }

    const signatureData = canvasRef.toDataURL();
    try {
      await signCanvas.mutateAsync(signatureData);
      setSigned(true);
      refetch();
    } catch {
      setValidationError('Uw handtekening is niet opgeslagen. Het document is nog niet ondertekend; probeer opnieuw.');
    }
  };

  const datum = (d: string) => new Date(d).toLocaleDateString('nl-BE', { timeZone: 'Europe/Brussels', day: 'numeric', month: 'long', year: 'numeric' });

  // Loading
  if (isLoading) {
    return <Melding laden titel="Uw document wordt geladen…" />;
  }

  // Error / not found
  if (error || !signing) {
    return (
      <Melding icoon="slot" titel="Deze link werkt niet meer">
        Dit verzoek om te ondertekenen bestaat niet meer of is verlopen. Bel of mail uw winkel, zij sturen u graag een nieuwe link.
      </Melding>
    );
  }

  const kop = (
    <header className="k-shell-header">
      <div className="max-w-lg mx-auto px-4 py-3 flex items-center justify-between gap-3">
        <KordaroLogo variant="primary" />
        <span className="k-meta">Ondertekenen</span>
      </div>
    </header>
  );

  // Already signed
  if (signing.status === 'SIGNED' || signed) {
    return (
      <div className="k-ui min-h-[100dvh]">
        {kop}
        <main className="max-w-lg mx-auto px-4 py-8 space-y-3" role="status">
          <p className="k-status k-status--success"><KIcon naam="vink" size={16} /> Ondertekend</p>
          <h1 className="k-title">Uw handtekening is opgeslagen</h1>
          <p>Het document <strong className="font-semibold break-words">{signing.document.naam}</strong> is ondertekend.</p>
          <p className="k-meta">Dossier {signing.dossier.dossierNummer} — {signing.dossier.klantNaam}</p>
          {signing.signedAt && (
            <p className="k-meta">
              Ondertekend op {new Date(signing.signedAt).toLocaleDateString('nl-BE', { timeZone: 'Europe/Brussels', day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </p>
          )}
        </main>
      </div>
    );
  }

  // Expired
  if (signing.status === 'EXPIRED') {
    return (
      <Melding icoon="klok" titel="Dit verzoek is verlopen">
        U kon dit document ondertekenen tot {datum(signing.expiresAt)}. Bel of mail uw winkel voor een nieuw verzoek.
      </Melding>
    );
  }

  // Cancelled
  if (signing.status === 'CANCELLED') {
    return (
      <Melding icoon="info" titel="Dit verzoek is ingetrokken">
        De winkel heeft dit verzoek om te ondertekenen ingetrokken. Hebt u een vraag? Bel of mail uw winkel.
      </Melding>
    );
  }

  // PENDING — signing flow
  return (
    <div className="k-ui min-h-[100dvh]">
      {kop}
      <main className="max-w-lg mx-auto px-4 py-6 space-y-6">
        <div>
          <h1 className="k-title">Document ondertekenen</h1>
          <p className="k-meta mt-1">Dossier {signing.dossier.dossierNummer} — {signing.dossier.klantNaam}</p>
        </div>

        <section aria-label="Document" className="flex gap-3 border-y border-[var(--k-border)] py-4">
          <span className="text-[var(--k-muted)]"><KIcon naam="documenten" size={24} /></span>
          <div className="min-w-0">
            <p className="font-semibold break-words">{signing.document.naam}</p>
            <p className="k-meta">{documentType(signing.document.documentType)} · aangevraagd door {signing.requestedBy.firstName} {signing.requestedBy.lastName}</p>
            <p className="k-meta">Geldig tot {datum(signing.expiresAt)}</p>
          </div>
        </section>

        {/* Canvas signing */}
        {signing.type === 'CANVAS' && (
          <section aria-labelledby="k-teken-titel" className="k-panel space-y-3">
            <h2 id="k-teken-titel" className="k-section-title">Teken uw handtekening</h2>
            <p>Teken hieronder met uw vinger, stylus of muis.</p>
            <PortaalSignatureCanvas onReady={setCanvasRef} />
            <p aria-live="polite" className="k-error">{validationError}</p>
            <button type="button" onClick={handleSign} disabled={signCanvas.isPending} aria-busy={signCanvas.isPending || undefined} className="k-button w-full">
              {signCanvas.isPending ? 'Bezig…' : 'Document ondertekenen'}
            </button>
          </section>
        )}

        {/* itsme: bestaat nog niet, enkel de bestaande melding */}
        {signing.type === 'ITSME' && (
          <section aria-labelledby="k-itsme-titel" className="k-panel space-y-3">
            <h2 id="k-itsme-titel" className="k-section-title">Ondertekenen met itsme</h2>
            <p>Ondertekenen met itsme is nog niet beschikbaar. U ontvangt een bericht zodra het kan.</p>
          </section>
        )}

        <p className="k-meta text-center">Beveiligd door Kordaro</p>
      </main>
    </div>
  );
}
